import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../../components/Modals/CustomModal";
import { Button } from "../../../../components/Buttons";
import InputField from "../../../../components/InputFields/InputField";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../../../utils/constants";
import { CampaignSettingsEndpoints } from "../../../../utils/constants/httpConstants";
import {
  postRequest,
  patchRequest,
} from "../../../../Apis/apiWrapper";
import { getErrorMessage } from "../../../../utils/helpers";
import { GREY } from "../../../../utils/constants/colors";
import { fontWeight } from "../../../../utils/constants/UI";
import { STRINGS } from "../../../Marketing/strings";
import { styles } from "./styles";

const GroupFormModal = ({
  open,
  onClose,
  onSaved,
  group,
  existingNames = [],
}) => {
  const classes = styles();
  const navigate = useNavigate();
  const isEdit = !!group?._id;

  const [name, setName] = useState(group?.group_name || "");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(group?.group_name || "");
      setErrorMessage("");
    }
  }, [open, group]);

  const validate = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return STRINGS.GROUP_NAME_REQUIRED;
    const lower = trimmed.toLowerCase();
    const conflict = existingNames.some(
      (entry) =>
        entry.group_name.toLowerCase() === lower && entry._id !== group?._id,
    );
    if (conflict) return STRINGS.GROUP_NAME_DUPLICATE;
    return "";
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    const err = validate(trimmed);
    if (err) {
      setErrorMessage(err);
      return;
    }
    try {
      setIsSaving(true);
      const response = isEdit
        ? await patchRequest(
            navigate,
            `${CampaignSettingsEndpoints.mailboxGroupRename}/${group._id}`,
            { group_name: trimmed },
          )
        : await postRequest(
            navigate,
            CampaignSettingsEndpoints.mailboxGroupCreate,
            { group_name: trimmed, account_ids: [] },
          );
      EventEmitter.emit(
        EMITTER_SUCCESS,
        response?.data?.message ||
          (isEdit ? "Group renamed" : "Group created"),
      );
      onSaved?.(response?.data?.data);
      onClose?.();
    } catch (apiErr) {
      // Surface duplicate-name errors inline on the field; everything else as a toast.
      const apiMessage = apiErr?.data?.message || "";
      const fieldErrors = apiErr?.data?.data?.errors || {};
      if (
        fieldErrors?.group_name ||
        /already exists|duplicate/i.test(apiMessage)
      ) {
        setErrorMessage(
          fieldErrors?.group_name || STRINGS.GROUP_NAME_DUPLICATE,
        );
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(fieldErrors, apiMessage),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const header = {
    heading: {
      id: "group-form",
      label: isEdit ? STRINGS.RENAME_GROUP : STRINGS.CREATE_GROUP,
    },
  };

  const body = (
    <Box className={classes.mgModalBody}>
      <TypographyInline
        size={"sm"}
        color={GREY.QUATINARY}
        fontWeight={fontWeight.MEDIUM}
        label={STRINGS.GROUP_NAME}
        isRequired
      />
      <InputField
        size={"sm36"}
        placeholder={STRINGS.GROUP_NAME_PLACEHOLDER}
        value={name}
        variant={errorMessage ? "error" : "default"}
        errormessage={errorMessage}
        onChange={(e) => {
          setName(e.target.value);
          if (errorMessage) setErrorMessage("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSubmit();
        }}
      />
    </Box>
  );

  const footerRight = (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <Button
        label={STRINGS.CANCEL}
        size={"sm36"}
        variant={"ghost"}
        onClick={onClose}
      />
      <Button
        label={isEdit ? STRINGS.ACTION_SAVE : STRINGS.ACTION_CREATE}
        size={"sm36"}
        variant={"primary"}
        onClick={handleSubmit}
        isLoading={isSaving}
      />
    </Box>
  );

  return (
    <CustomModal
      open={open}
      close={onClose}
      header={header}
      children={body}
      footer={{ left: <div />, right: footerRight }}
      width="420px"
    />
  );
};

export default GroupFormModal;
